import styled from "styled-components";
import BodySmall from "../atoms/typography/BodySmall";
import { Row } from "react-grid-system";
import { theme } from "../../theme";
import {
  ChevronLeftIconLarge,
  ChevronRightIconLarge,
} from "../atoms/icons/ChevronIcons";

interface Props {
  numberOfPages: number;
  currentPageIndex: number;
  setCurrentPageIndex: (index: number) => void;
  align?: "left" | "center" | "right";
  spacing?: number;
}

const Container = styled.div<{ $align: string; $spacing?: number }>`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: ${(props) =>
    props.$align === "left"
      ? "flex-start"
      : props.$align === "right"
        ? "flex-end"
        : "center"};
  ${(props) =>
    props.$spacing !== undefined
      ? `padding-top: ${props.$spacing}rem; padding-bottom: ${props.$spacing}rem;`
      : ""}
`;

const ChevronButton = styled.button`
  display: flex;
  align-items: center;
  border: none;
  cursor: pointer;
  background-color: transparent;
  padding: 0;

  &:hover {
    filter: opacity(0.6);
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const NumberItem = styled.button<{ selected: boolean }>`
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  cursor: pointer;

  background-color: ${(props) =>
    props.selected ? theme.colors.primary : theme.colors.white};

  &:hover {
    cursor: pointer;
    background-color: ${theme.colors.primary20};
  }
  margin: 4px;

  &:disabled {
    cursor: not-allowed;
    background-color: transparent;
  }
`;

const PaginationComponent: React.FC<Props> = ({
  numberOfPages,
  currentPageIndex,
  setCurrentPageIndex,
  align = "center",
  spacing,
}) => {
  const onClickPage = (page: number) => {
    setCurrentPageIndex(page);
  };
  const renderPageNumbers = () => {
    const pages = [];
    const ellipsis = (key: string) => <BodySmall key={key}>...</BodySmall>;

    const showPage = (i: number) => (
      <NumberItem
        key={i}
        selected={currentPageIndex === i}
        onClick={() => onClickPage(i)}
      >
        <BodySmall
          color={
            currentPageIndex === i ? theme.colors.white : theme.colors.text
          }
        >
          {i + 1}
        </BodySmall>
      </NumberItem>
    );

    if (numberOfPages <= 7) {
      for (let i = 0; i < numberOfPages; i++) {
        pages.push(showPage(i));
      }
    } else {
      const nearStart = currentPageIndex <= 3;
      const nearEnd = currentPageIndex >= numberOfPages - 4;

      pages.push(showPage(0));

      if (nearStart) {
        for (let i = 1; i <= 4; i++) {
          pages.push(showPage(i));
        }
        pages.push(ellipsis("end"));
      } else if (nearEnd) {
        pages.push(ellipsis("start"));
        for (let i = numberOfPages - 5; i < numberOfPages - 1; i++) {
          pages.push(showPage(i));
        }
      } else {
        pages.push(ellipsis("start"));
        for (let i = currentPageIndex - 1; i <= currentPageIndex + 1; i++) {
          pages.push(showPage(i));
        }
        pages.push(ellipsis("end"));
      }

      pages.push(showPage(numberOfPages - 1));
    }

    return pages;
  };

  const handlePreviousPage = () => {
    if (currentPageIndex > 0) {
      onClickPage(currentPageIndex - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPageIndex < numberOfPages - 1) {
      onClickPage(currentPageIndex + 1);
    }
  };

  if (numberOfPages <= 1) return null;

  return (
    <Container $align={align} $spacing={spacing}>
      <ChevronButton
        onClick={handlePreviousPage}
        disabled={currentPageIndex === 0}
      >
        <ChevronLeftIconLarge
          color={
            currentPageIndex === 0
              ? theme.colors.disabled
              : theme.colors.primary
          }
        />
      </ChevronButton>
      <Row justify="center" nogutter align="center">
        {renderPageNumbers()}
      </Row>
      <ChevronButton
        onClick={handleNextPage}
        disabled={currentPageIndex === numberOfPages - 1}
      >
        <ChevronRightIconLarge
          color={
            currentPageIndex === numberOfPages - 1
              ? theme.colors.disabled
              : theme.colors.primary
          }
        />
      </ChevronButton>
    </Container>
  );
};

export default PaginationComponent;
